import useMediaQuery from "@mui/material/useMediaQuery";
import { useClickAway, useIsClient } from "@uidotdev/usehooks";
import { debounce } from "lodash";
import { useRouter } from "next/router";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  convertIdsToUrl,
  processJobSearchKeyword,
} from "../../../helpers/filter_management";
import {
  fetchSearchSuggestions,
  updateJobListFilter,
  updateJobSearchKeyword,
} from "../../../redux/actions/job_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import JobSearchOptionsMenu from "./JobSearchOptionsMenu/JobSearchOptionsMenu";
import {
  BadgeStyled,
  BriefcaseIcon,
  ClearButton,
  ClearIcon,
  FilterButton,
  FilterIcon,
  FypIcon,
  JobsForYouButton,
  JobsForYouLinkButton,
  LocationBarFlat,
  LocationCount,
  LocationInput,
  MapPinIconStyled,
  MobileHeaderRow,
  MobileSearchContainer,
  MobileTitle,
  SearchBarFlat,
  // InternshipIcon,
  SearchButton,
  SearchDropdownContainer,
  SearchIcon,
  SearchInput,
  SearchOptionsWrapper,
  SearchSuggestionContainer,
  UnifiedSearchBar,
  VerticalDivider,
  Wrapper,
} from "./styles";

/**
 * JobSearchOptions Component
 * Main search interface for jobs including a search bar, filter button and internship toggle.
 * Provides functionality for searching jobs by keyword and managing filter states.
 * Responsive design that adapts between mobile and desktop views.
 */
export default forwardRef(function JobSearchOptions(
  {
    handleJobsForYouClick = () => {},
    currentTab = "job-list",
    externalFilterAnchorRef = null,
  } = {},
  ref
) {
  const isClient = useIsClient();
  const isUser = checkAuthentication();
  const { t } = useTranslation();

  const router = useRouter();
  const isDesktop = useMediaQuery("(min-width: 641px)");
  const isLargeDesktop = useMediaQuery("(min-width: 1280px)");
  const isMobile = useMediaQuery("(max-width: 640px)");

  const internalFilterButtonRef = useRef(null);
  const filterButtonRef = externalFilterAnchorRef || internalFilterButtonRef;

  const dispatch = useDispatch();

  const isLoadingJobs = useSelector((state) => state.jobs?.isLoadingJobs);

  const jobListFilter = useSelector((state) => state.jobs?.jobListFilter);

  const currentSearch = useSelector((state) => state.jobs?.jobSearchKeyword);
  const searchSuggestion = useSelector((state) => state.jobs?.searchData);

  const specialisationList = useSelector((state) => state.jobs?.specialisation);
  const stateList = useSelector((state) => state.jobs?.stateRegions);
  const jobTypeList = useSelector((state) => state.jobs?.jobTypes);

  const selectedLocations = jobListFilter?.state || [];
  const locationCount = selectedLocations.length;

  const [location, setLocation] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [locationFocus, setLocationFocus] = useState(false);
  const locationButtonRef = useRef(null);
  const locationMenuRef = useRef(null);
  const [locationOpen, setLocationOpen] = useState(false);
  const searchAreaRef = useClickAway((event) => {
    if (locationMenuRef.current?.contains(event.target)) return;
    setSearchBarFocus(false);
  });

  // Filter Button State
  const [searchBarFocus, setSearchBarFocus] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [menuMode, setMenuMode] = useState("filter");
  const [resetKey, setResetKey] = useState(0);

  //DYNAMIC CONST
  const getLocationLabel = (selected = []) => {
    if (!selected?.length) return "";
    if (selected.length === 1) return selected[0];
    return t("jobs.location");
  };

  const totalFilter = useMemo(() => {
    const {
      specialisation = [],
      jobType = [],
      experience = [],
      salary = 0,
      globalHire = false,
    } = jobListFilter;

    const total =
      specialisation?.length +
      jobType?.length +
      experience?.length +
      (salary > 0 ? 1 : 0) +
      (globalHire ? 1 : 0);
    return total;
  }, [jobListFilter]);

  const filterActive = totalFilter > 0 && Object.keys(jobListFilter).length > 0;
  // const internshipActive = jobListFilter.jobType?.includes(4);

  const debounceSearchSuggestions = debounce((params) => {
    dispatch(fetchSearchSuggestions(params));
  }, 300);

  // FUNCTION LOGIC
  // SEARCH BAR LOGIC
  // handle search input focus state
  function handleFocusSearch() {
    setSearchBarFocus(true);
  }

  // handle search input changes
  function handleInputSearch(e) {
    const keyword = e.target.value;

    dispatch(updateJobSearchKeyword({ keyword: keyword }));
    debounceSearchSuggestions({ keyword: keyword });
  }

  // handle search suggestion and highlight the matching text
  function boldMatchingText(suggestion) {
    const keyword = currentSearch.toLowerCase();

    const lowerCaseSuggestion = suggestion.toLowerCase();
    const index = lowerCaseSuggestion.indexOf(keyword);

    if (!keyword || index === -1) {
      return (
        <SearchSuggestionContainer
          onClick={() => handleTriggerSearch(suggestion)}
        >
          {suggestion}
        </SearchSuggestionContainer>
      );
    }

    const beforeMatch = suggestion.substring(0, index);
    const match = suggestion.substring(index, index + keyword.length);
    const afterMatch = suggestion.substring(index + keyword.length);

    return (
      <SearchSuggestionContainer
        onClick={() => handleTriggerSearch(suggestion)}
      >
        <span>
          {beforeMatch}
          <b>{match}</b>
          {afterMatch}
        </span>
      </SearchSuggestionContainer>
    );
  }

  // clear the current search input
  function handleClearSearch() {
    const selectedFilter = {
      ...jobListFilter,
      keyword: "",
    };

    const { path, query } = convertIdsToUrl(
      selectedFilter,
      specialisationList,
      stateList
    );

    router.push({ pathname: path, query }, undefined, { shallow: false });
    dispatch(updateJobSearchKeyword({ keyword: "" }));
    dispatch(updateJobListFilter(selectedFilter));
    setSearchBarFocus(false);
    setResetKey((prevKey) => prevKey + 1);

    // if (isUser) {
    //   const { path, query } = convertIdsToUrl(
    //     selectedFilter,
    //     specialisationList,
    //     stateList
    //   );

    //   router.push({ pathname: path, query }, undefined, { shallow: false });
    //   dispatch(updateJobSearchKeyword({ keyword: "" }));
    //   dispatch(updateJobListFilter(selectedFilter));
    //   setSearchBarFocus(false);
    //   setResetKey((prevKey) => prevKey + 1);
    // } else {
    //   if (selectedFilter?.state?.length > 0) {
    //     router.push({ pathname: path, query }, undefined, { shallow: false });
    //     dispatch(updateJobSearchKeyword({ keyword: "" }));
    //     dispatch(updateJobListFilter(selectedFilter));
    //   } else {
    //     dispatch(updateJobSearchKeyword({ keyword: "" }));
    //   }
    // }
  }

  // handle enter key press
  function handleEnterSearch(e) {
    if (e.key === "Enter") {
      handleTriggerSearch();
    }
  }

  // handle trigger search
  function handleTriggerSearch(suggestion = null) {
    setFilterOpen(false);

    const keyword = suggestion ?? currentSearch;
    const currentFilter = jobListFilter;

    const { updatedFilter, foundMatchingResults } = processJobSearchKeyword(
      keyword,
      currentFilter,
      specialisationList,
      stateList,
      jobTypeList
    );

    if (foundMatchingResults) {
      dispatch(updateJobSearchKeyword({ keyword: "" }));
    } else {
      dispatch(updateJobSearchKeyword({ keyword: keyword }));
    }

    if (updatedFilter) setResetKey((prevKey) => prevKey + 1);

    const { path, query } = convertIdsToUrl(
      updatedFilter,
      specialisationList,
      stateList
    );

    router.push({ pathname: path, query }, undefined, { shallow: false });
    setSearchBarFocus(false);
    dispatch(updateJobListFilter(updatedFilter));
  }

  // FILTER LOGIC
  // open and close the filter menu
  function handleClickFilter() {
    setMenuMode("filter");
    setFilterOpen(!filterOpen);
    setLocationOpen(false);
  }

  function handleOpenLocationMenu() {
    setMenuMode("location");
    setLocationOpen(true);
    setLocationFocus(true);
    setFilterOpen(false);
  }

  function handleFocusLocation() {
    if (!locationOpen) {
      handleOpenLocationMenu();
      return;
    }
    setLocationFocus(true);
  }

  function handleChangeLocation(value) {
    setLocation(value);
    setLocationSearch(value);
    handleOpenLocationMenu();
  }

  function handleClearLocation(event) {
    event?.stopPropagation?.();

    const clearedFilter = {
      ...jobListFilter,
      state: [],
    };

    setLocation("");
    setLocationSearch("");
    if (locationOpen) {
      setLocationFocus(true);
    } else {
      setLocationOpen(false);
      setLocationFocus(false);
    }

    const { path, query } = convertIdsToUrl(
      clearedFilter,
      specialisationList,
      stateList
    );

    router.push({ pathname: path, query }, undefined, { shallow: false });
    dispatch(updateJobListFilter(clearedFilter));

    // if (isUser) {
    //   router.push({ pathname: path, query }, undefined, { shallow: false });
    //   dispatch(updateJobListFilter(clearedFilter));
    // } else {
    //   if (clearedFilter?.keyword?.length > 0) {
    //     router.push({ pathname: path, query }, undefined, { shallow: false });
    //     dispatch(updateJobListFilter(clearedFilter));
    //   } else {
    //     dispatch(updateJobListFilter(clearedFilter));
    //   }
    // }
  }

  // // apply and remove the internship filter
  // function handleClickInternship() {
  //   if (internshipActive) {
  //     // remove job type 4 from the filter
  //     const removeInternshipFilter = jobListFilter.jobType.filter(
  //       (jobType) => jobType !== 4
  //     );

  //     const newJobListFilter = {
  //       ...jobListFilter,
  //       jobType: removeInternshipFilter,
  //     };

  //     const { path, query } = convertIdsToUrl(
  //       newJobListFilter,
  //       specialisationList,
  //       stateList
  //     );

  //     dispatch(updateJobListFilter(newJobListFilter));

  //     router.push({
  //       pathname: path,
  //       query: query,
  //       shallow: false,
  //     });
  //   } else {
  //     if (!jobListFilter.jobType) {
  //       jobListFilter.jobType = [];
  //     }

  //     // add job type 4 to the filter
  //     const addInternshipFilter = jobListFilter.jobType.concat(4);

  //     const newJobListFilter = {
  //       ...jobListFilter,
  //       jobType: addInternshipFilter,
  //     };

  //     const { path, query } = convertIdsToUrl(
  //       newJobListFilter,
  //       specialisationList,
  //       stateList
  //     );

  //     dispatch(updateJobListFilter(newJobListFilter));

  //     router.push({
  //       pathname: path,
  //       query: query,
  //       shallow: false,
  //     });
  //   }
  // }

  // handle apply search
  function handleApplyFilter(selectedFilter) {
    setFilterOpen(false);
    setLocationOpen(false);
    setLocationFocus(false);

    // Include the current search keyword if it exists
    let updatedFilter = { ...selectedFilter };

    if (currentSearch && currentSearch.trim()) {
      // If there's a search keyword, process it and include in the filter
      const { updatedFilter: processedFilter, foundMatchingResults } =
        processJobSearchKeyword(
          currentSearch,
          selectedFilter,
          specialisationList,
          stateList,
          jobTypeList
        );

      updatedFilter = processedFilter;

      // Clear the search input if matching results were found, otherwise keep it
      if (foundMatchingResults) {
        dispatch(updateJobSearchKeyword({ keyword: "" }));
      }
    } else {
      // If no search keyword, ensure keyword is removed from filter
      updatedFilter = { ...selectedFilter, keyword: "" };
      dispatch(updateJobSearchKeyword({ keyword: "" }));
    }

    const { path, query } = convertIdsToUrl(
      updatedFilter,
      specialisationList,
      stateList
    );

    router.push({ pathname: path, query }, undefined, { shallow: false });
    dispatch(updateJobListFilter(updatedFilter));
  }

  // set the search bar focus to false when clicking away
  useEffect(() => {
    // if (locationOpen) return;
    const selectedLocation = jobListFilter?.state || [];
    setLocation(getLocationLabel(selectedLocation));
  }, [jobListFilter?.state, locationOpen]);

  useEffect(() => {
    if (!locationOpen) {
      setLocationFocus(false);
      setLocationSearch("");
    }
  }, [locationOpen]);

  useImperativeHandle(ref, () => ({
    openFilter: handleClickFilter,
  }));

  useEffect(() => {
    if (!locationOpen) return;

    function handleClickOutside(event) {
      if (
        locationButtonRef.current?.contains(event.target) ||
        locationMenuRef.current?.contains(event.target)
      ) {
        return;
      }
      setLocationOpen(false);
      setLocationFocus(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [locationOpen]);

  return isClient ? (
    <Wrapper>
      <SearchOptionsWrapper>
        {isDesktop ? (
          <UnifiedSearchBar ref={searchAreaRef} disabled={isLoadingJobs}>
            <SearchBarFlat>
              <BriefcaseIcon />
              <SearchInput
                value={currentSearch}
                data-testid="job-search-input"
                onChange={handleInputSearch}
                onClick={handleFocusSearch}
                onKeyDown={handleEnterSearch}
                placeholder={t("jobs.search_jobs")}
              />
              {currentSearch && (
                <ClearButton onClick={handleClearSearch}>
                  <ClearIcon />
                </ClearButton>
              )}

              {searchBarFocus &&
              currentSearch?.length > 2 &&
              searchSuggestion?.length > 0 ? (
                <SearchDropdownContainer>
                  {searchSuggestion.map((data, index) => {
                    return <div key={index}>{boldMatchingText(data.name)}</div>;
                  })}
                </SearchDropdownContainer>
              ) : null}
            </SearchBarFlat>

            <VerticalDivider />

            <LocationBarFlat
              ref={locationButtonRef}
              $active={locationFocus}
              disabled={isLoadingJobs}
              onClick={handleOpenLocationMenu}
            >
              <MapPinIconStyled />
              <LocationInput
                value={location}
                onFocus={handleFocusLocation}
                onChange={(e) => handleChangeLocation(e.target.value)}
                placeholder={t("jobs.enter_location")}
              />
              {locationCount > 1 ? (
                <LocationCount>{locationCount}</LocationCount>
              ) : null}
              {(location?.length > 0 || locationCount > 0) && (
                <ClearButton onClick={(e) => handleClearLocation(e)}>
                  <ClearIcon />
                </ClearButton>
              )}
            </LocationBarFlat>

            <SearchButton onClick={() => handleTriggerSearch()} data-testid="job-search-btn">
              <SearchIcon />
            </SearchButton>
          </UnifiedSearchBar>
        ) : (
          <MobileSearchContainer ref={searchAreaRef}>
            <MobileHeaderRow>
              <MobileTitle $active={currentTab === "job-list"} data-testid="job-search-trigger-btn">
                <SearchIcon />
                <span>{t("jobs.search")}</span>
              </MobileTitle>
              <JobsForYouLinkButton
                onClick={() => handleJobsForYouClick()}
                disabled={isLoadingJobs}
                $active={currentTab === "for-you"}
              >
                <span>{t("jobs.jobs_for_you")}</span>
                <FypIcon />
              </JobsForYouLinkButton>
            </MobileHeaderRow>
          </MobileSearchContainer>
        )}
        {isLargeDesktop && currentTab === "job-list" ? (
          <BadgeStyled
            badgeContent={totalFilter}
            max={9}
            color="primary"
          >
            <FilterButton
              ref={filterButtonRef}
              data-testid="job-filter-toggle-btn"
              $active={filterActive}
              onClick={handleClickFilter}
              disabled={isLoadingJobs}
            >
              {!isMobile ? t("jobs.filter") : null}
              <FilterIcon />
            </FilterButton>
          </BadgeStyled>
        ) : null}
        {/* <FilterButton
          $active={internshipActive}
          onClick={handleClickInternship}
          disabled={isLoadingJobs}
        >
          {!isMobile ? "Internship" : null}
          <InternshipIcon />
        </FilterButton> */}
        {/* jobs for you tab */}
        {isDesktop ? (
          <JobsForYouButton
            onClick={() => handleJobsForYouClick()}
            disabled={isLoadingJobs}
            $active={currentTab === "for-you"}
          >
            {!isMobile ? t("jobs.jobs_for_you") : null}
            <FypIcon />
          </JobsForYouButton>
        ) : null}
      </SearchOptionsWrapper>

      <JobSearchOptionsMenu
        filterOpen={filterOpen || locationOpen}
        setFilterOpen={(open) => {
          // close both when menu closes
          setFilterOpen(open);
          setLocationOpen(open);
          if (!open) setLocationFocus(false);
        }}
        anchorRef={
          menuMode === "location" ? locationButtonRef : filterButtonRef
        }
        mode={menuMode}
        onApply={handleApplyFilter}
        locationSearch={locationSearch}
        menuRef={locationMenuRef}
      />
    </Wrapper>
  ) : null;
});
